import { Box, Chip, LinearProgress, Stack, Tooltip, Typography } from "@mui/material"
import WarningAmberIcon from "@mui/icons-material/WarningAmber"
import TrendingUpIcon from "@mui/icons-material/TrendingUp"
import { FC, ReactNode } from "react"
import { useTranslation } from "react-i18next"
import {
  Sl1AnomalySeverity,
  Sl1ProviderState,
  Sl1Resolution,
} from "shared/sl1ImportData"
import { Sl1ImportJobDto } from "src/features/things/sl1ImportApi"

/**
 * The SL1-import console's shared visual vocabulary (LOCKER-382 child D).
 *
 * Its own file because all three surfaces use it — a shared primitive living
 * inside one of its three consumers means deleting that consumer breaks the other
 * two for a non-obvious reason (`.claude/rules/frontend-conventions.md`, and the
 * same argument `evidenceStatusColor.ts` makes for itself).
 *
 * ## No yellow, no amber, anywhere as a FILL
 *
 * Warning states are muted blue/slate with a warning ICON, never a yellow block
 * (repo convention: `no-harsh-yellow-ui`). `WarningAmberIcon` is imported for its
 * glyph only; every fill below comes from the palette so both themes work — which
 * is also why nothing here hardcodes `bgcolor: "white"` the way
 * `Sstv2FleetRollup`'s `cardSx` does.
 */

/** Panel chrome that survives a theme flip, unlike a hardcoded white. */
export const panelSx = {
  bgcolor: "background.paper",
  border: 1,
  borderColor: "divider",
  borderRadius: 2,
} as const

export const eyebrowSx = {
  fontSize: "0.7rem",
  fontWeight: 700,
  letterSpacing: "0.1em",
  textTransform: "uppercase" as const,
  color: "text.secondary",
}

export const fmtNum = (n: number | null | undefined): string =>
  n === null || n === undefined ? "—" : n.toLocaleString()

export const fmtBytes = (b: number | undefined): string =>
  b === undefined
    ? "—"
    : b >= 1048576
      ? `${(b / 1048576).toFixed(1)} MB`
      : b >= 1024
        ? `${Math.round(b / 1024)} KB`
        : `${b} B`

/** `2026-08-26 12:04:11`, or an em dash. Never a locale string — these are logs. */
export const fmtStamp = (iso: string | undefined): string =>
  iso ? iso.replace("T", " ").slice(0, 19) : "—"

// ---------------------------------------------------------------------------
// Status chips
// ---------------------------------------------------------------------------

/**
 * Run status → chip colour.
 *
 * `NEEDS_REVIEW` and `PARTIAL` are `info`, NOT `warning`: MUI's warning palette
 * is amber, and a table with six amber blocks reads as six failures when it is
 * actually six things waiting on a human. `BLOCKED` is `error` because it IS
 * stop-work; `SUPERSEDED` is `default` because a superseded run is not bad, it is
 * simply no longer the answer.
 */
export const sl1JobStatusColor = (
  s: Sl1ImportJobDto["status"],
): "success" | "info" | "error" | "default" =>
  s === "COMPLETED"
    ? "success"
    : s === "FAILED" || s === "BLOCKED"
      ? "error"
      : s === "SUPERSEDED"
        ? "default"
        : "info"

export const Sl1JobStatusChip: FC<{
  status: Sl1ImportJobDto["status"]
  /**
   * The run's kind, when known.
   *
   * `COMPLETED` is labelled "Clean" — a VERDICT word, which is right for a recon
   * and wrong for an apply: an apply grades nothing, it writes. Reading "Clean"
   * against a run that put thousands of rows into production invites it to be
   * taken as an assessment of those rows. Same status, honest label.
   */
  kind?: Sl1ImportJobDto["kind"]
  size?: "small" | "medium"
}> = ({ status, kind, size = "small" }) => {
  const { t } = useTranslation()
  const color = sl1JobStatusColor(status)
  return (
    <Chip
      size={size}
      label={
        kind === "apply" && status === "COMPLETED"
          ? t("sl1JobStatus_COMPLETED_apply")
          : t(`sl1JobStatus_${status}`)
      }
      color={color}
      // Outlined for the two neutral rungs so a list of mostly-COMPLETED runs
      // does not become a wall of solid colour with nothing standing out.
      variant={status === "SUPERSEDED" || status === "COMPLETED" ? "outlined" : "filled"}
      icon={status === "BLOCKED" ? <WarningAmberIcon fontSize="small" /> : undefined}
    />
  )
}

/**
 * Provider state → chip.
 *
 * `NEEDS_MAPPING` gets its own treatment and it is deliberately NOT an error:
 * nothing links an SL1 tenant to an SL2 tenant yet (Decision 1), so an unmapped
 * provider is a normal reading of a fleet nobody has mapped. Rendering six of
 * them in red would put an operator who has done nothing wrong in front of a wall
 * of failures.
 */
export const sl1ProviderStateColor = (
  s: Sl1ProviderState,
): "success" | "info" | "error" | "default" | "primary" =>
  s === "IMPORTED"
    ? "success"
    : s === "BLOCKED"
      ? "error"
      : s === "NEEDS_MAPPING"
        ? "default"
        : s === "NOT_STARTED"
          ? "default"
          : "info"

export const Sl1ProviderStateChip: FC<{ state: Sl1ProviderState }> = ({
  state,
}) => {
  const { t } = useTranslation()
  return (
    <Chip
      size="small"
      label={t(`sl1ProviderState_${state}`)}
      color={sl1ProviderStateColor(state)}
      variant={
        state === "NEEDS_MAPPING" || state === "NOT_STARTED"
          ? "outlined"
          : "filled"
      }
      icon={
        state === "NEEDS_MAPPING" ? (
          <WarningAmberIcon fontSize="small" />
        ) : undefined
      }
      sx={
        state === "NEEDS_MAPPING"
          ? { borderStyle: "dashed", color: "text.secondary" }
          : undefined
      }
    />
  )
}

/** Anomaly severity → the stripe colour used on the anomalies table. */
export const sl1SeverityColor = (s: Sl1AnomalySeverity): string =>
  s === "blocked" ? "error.main" : "info.main"

/**
 * A low-saturation pill.
 *
 * Used for anomaly categories and resolutions, where a dozen may sit in one
 * table: MUI's filled chips at full saturation turn a data table into a paint
 * chart, and the reader loses the numbers. The severity is carried by a solid
 * left stripe on the ROW instead, which is one strong signal rather than twelve.
 */
export const Sl1Pill: FC<{
  label: string
  tone?: "neutral" | "info" | "error" | "success"
  title?: string
}> = ({ label, tone = "neutral", title }) => {
  const color =
    tone === "error"
      ? "error.main"
      : tone === "success"
        ? "success.main"
        : tone === "info"
          ? "info.main"
          : "text.secondary"
  const pill = (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        px: 0.9,
        py: 0.15,
        borderRadius: 1,
        border: 1,
        borderColor: color,
        color,
        // 12% of the tone, so a dozen of these stay readable side by side.
        bgcolor: "transparent",
        opacity: 0.95,
        fontSize: "0.68rem",
        fontWeight: 700,
        letterSpacing: "0.04em",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Box>
  )
  return title ? <Tooltip title={title}>{pill}</Tooltip> : pill
}

export const sl1ResolutionTone = (
  r: Sl1Resolution,
): "neutral" | "info" | "error" | "success" =>
  r === "matched_exact"
    ? "success"
    : r === "blocked"
      ? "error"
      : r === "unmatched"
        ? "info"
        : "neutral"

// ---------------------------------------------------------------------------
// Tiles + meters
// ---------------------------------------------------------------------------

export const Sl1Tile: FC<{
  label: string
  value: ReactNode
  hint?: string
  /** A dot, not a fill — the tile stays legible in both themes at any count. */
  dotColor?: string
}> = ({ label, value, hint, dotColor = "primary.main" }) => (
  <Box sx={{ ...panelSx, p: 2 }}>
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: dotColor }} />
      <Typography sx={eyebrowSx}>{label}</Typography>
    </Stack>
    <Typography sx={{ fontSize: "1.7rem", fontWeight: 700, lineHeight: 1.1 }}>
      {value}
    </Typography>
    {hint && (
      <Typography variant="caption" sx={{ color: "text.disabled" }}>
        {hint}
      </Typography>
    )}
  </Box>
)

/**
 * The fleet meter — imported of found, across the estate.
 *
 * **Drift is deliberately absent from this number.** SL1 is still live
 * (`OrderController.submit()` keeps writing locker rows), so a provider is never
 * finished, only current — and folding drift into a single estate-wide percentage
 * is exactly how that fact stops being visible. Drift is shown per provider, on
 * the row it belongs to. The meter's job is "how far through are we", not "are we
 * done".
 */
export const Sl1FleetMeter: FC<{
  imported: number
  matched: number
  found: number
  scannedAt?: string
}> = ({ imported, matched, found, scannedAt }) => {
  const { t } = useTranslation()
  const covered = imported + matched
  const pct = found > 0 ? Math.min(100, (covered / found) * 100) : 0
  return (
    <Box sx={{ ...panelSx, p: 2.5 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="baseline"
        sx={{ mb: 1 }}
      >
        <Typography sx={eyebrowSx}>{t("sl1FleetMeter")}</Typography>
        <Typography sx={{ fontWeight: 700 }}>{pct.toFixed(1)}%</Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: 10,
          borderRadius: 5,
          bgcolor: "action.hover",
          "& .MuiLinearProgress-bar": { bgcolor: "primary.main" },
        }}
      />
      <Typography
        variant="caption"
        sx={{ display: "block", mt: 1, color: "text.secondary" }}
      >
        {t("sl1FleetMeterHint", {
          covered: fmtNum(covered),
          found: fmtNum(found),
        })}
      </Typography>
      {scannedAt && (
        <Typography variant="caption" sx={{ color: "text.disabled" }}>
          {t("sl1ScannedAt", { at: fmtStamp(scannedAt) })}
        </Typography>
      )}
    </Box>
  )
}

/**
 * The per-provider drift marker.
 *
 * `undefined` renders NOTHING rather than a zero: "nobody has reconned this
 * provider" and "we reconned it and SL1 has not written since" are different
 * readings, and only one of them is reassuring. Collapsing them into `0` would
 * tell an operator a provider is stable when in fact it has never been looked at.
 */
export const Sl1DriftMarker: FC<{ drift: number | undefined }> = ({ drift }) => {
  const { t } = useTranslation()
  if (drift === undefined) {
    return (
      <Typography variant="caption" sx={{ color: "text.disabled" }}>
        {t("sl1DriftNeverReconned")}
      </Typography>
    )
  }
  if (drift === 0) {
    return (
      <Typography variant="caption" sx={{ color: "text.disabled" }}>
        {t("sl1DriftNone")}
      </Typography>
    )
  }
  return (
    <Tooltip title={t("sl1DriftHint")}>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <TrendingUpIcon sx={{ fontSize: 14, color: "info.main" }} />
        <Typography
          variant="caption"
          sx={{ color: "info.main", fontWeight: 700 }}
        >
          {t("sl1DriftNew", { n: fmtNum(drift) })}
        </Typography>
      </Stack>
    </Tooltip>
  )
}
